import { create } from 'zustand';
import type { ShopOffer } from '../types/shop.types';

const STORAGE_KEY = 'usagi_shop_stock';

/**
 * Rolled stock of the "Vendeur" (rolling apothecary). `offers` is the current
 * cycle's random selection; `soldItemIds` are the items already bought this
 * cycle (removed from the list until the next refill). Both are reset on
 * `setStock` (a fresh roll). cf. `ShopService`.
 */
interface ShopStockState {
  offers: ShopOffer[];
  soldItemIds: string[];
}

interface ShopStockActions {
  /** Replace the stock with a fresh roll (clears sold markers). */
  setStock: (offers: ShopOffer[]) => void;
  /** Mark an item as bought this cycle (removed from the list). */
  recordSold: (itemId: string) => void;
}

function load(): ShopStockState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ShopStockState>;
      return {
        offers: Array.isArray(parsed.offers) ? parsed.offers : [],
        soldItemIds: Array.isArray(parsed.soldItemIds)
          ? parsed.soldItemIds
          : [],
      };
    }
  } catch {
    /* ignore */
  }
  return { offers: [], soldItemIds: [] };
}

function persist(state: ShopStockState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* localStorage may be unavailable */
  }
}

export const useShopStockStore = create<ShopStockState & ShopStockActions>(
  (set, get) => ({
    ...load(),

    setStock: (offers) => {
      const next: ShopStockState = { offers, soldItemIds: [] };
      persist(next);
      set(next);
    },

    recordSold: (itemId) => {
      if (get().soldItemIds.includes(itemId)) {
        return;
      }
      const soldItemIds = [...get().soldItemIds, itemId];
      persist({ offers: get().offers, soldItemIds });
      set({ soldItemIds });
    },
  }),
);
